import React from 'react';
import PropTypes from 'prop-types';

/**
 * Button - A standardized button component with consistent styling across the app
 *
 * @param {Object}  props
 * @param {string}  props.variant  - primary | secondary | danger | success | warning | info | text | action
 * @param {string}  props.size     - sm | md | lg
 * @param {boolean} props.fullWidth
 * @param {boolean} props.disabled
 * @param {boolean} props.outlined - render the outlined (ghost) version of the variant
 * @param {React.ReactNode} props.icon - icon node placed before children
 */
const Button = ({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  disabled = false,
  children,
  onClick,
  type = 'button',
  className = '',
  icon,
  outlined = false,
  ...props
}) => {
  // Async handlers get a spinner automatically while their promise is pending.
  const [autoBusy, setAutoBusy] = React.useState(false);
  const mountedRef = React.useRef(true);
  React.useEffect(() => () => { mountedRef.current = false; }, []);

  const busy = props.loading === true || autoBusy;
  const { loading: _loading, busyLabel, ...rest } = props;

  const handleClick = async (event) => {
    if (!onClick) return;
    const result = onClick(event);
    if (!result || typeof result.then !== 'function') return;
    setAutoBusy(true);
    try {
      await result;
    } finally {
      if (mountedRef.current) setAutoBusy(false);
    }
  };
  const base =
    'inline-flex items-center justify-center font-medium rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1';

  const sizes = {
    sm: 'px-3 py-1.5 text-xs gap-1.5',
    md: 'px-4 py-2 text-sm gap-2',
    lg: 'px-5 py-2.5 text-base gap-2',
  };

  // Filled variants (default)
  const filled = {
    primary:   'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500 shadow-sm',
    secondary: 'bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-300 border border-gray-200 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600 dark:border-slate-600',
    danger:    'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 shadow-sm',
    success:   'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500 shadow-sm',
    warning:   'bg-amber-500 text-white hover:bg-amber-600 focus:ring-amber-400 shadow-sm',
    info:      'bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500 shadow-sm',
    text:      'bg-transparent text-blue-600 hover:bg-blue-50 hover:text-blue-700 focus:ring-blue-300 dark:text-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-300',
    // Small bordered button for table / toolbar row actions
    action:    'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 hover:text-gray-900 focus:ring-blue-400 shadow-sm dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700 dark:hover:bg-slate-700 dark:hover:text-slate-100',
  };

  // Ghost / outlined variants
  const ghost = {
    primary:   'bg-white text-blue-600 border border-blue-300 hover:bg-blue-50 focus:ring-blue-400 dark:bg-transparent dark:text-blue-400 dark:border-blue-700 dark:hover:bg-blue-900/20',
    secondary: 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50 focus:ring-gray-300 dark:bg-transparent dark:text-slate-300 dark:border-slate-600 dark:hover:bg-slate-800',
    danger:    'bg-white text-red-600 border border-red-300 hover:bg-red-50 focus:ring-red-400 dark:bg-transparent dark:text-red-400 dark:border-red-700 dark:hover:bg-red-900/20',
    success:   'bg-white text-green-600 border border-green-300 hover:bg-green-50 focus:ring-green-400 dark:bg-transparent dark:text-green-400 dark:border-green-700 dark:hover:bg-green-900/20',
    warning:   'bg-white text-amber-600 border border-amber-300 hover:bg-amber-50 focus:ring-amber-400 dark:bg-transparent dark:text-amber-400 dark:border-amber-700 dark:hover:bg-amber-900/20',
    info:      'bg-white text-indigo-600 border border-indigo-300 hover:bg-indigo-50 focus:ring-indigo-400 dark:bg-transparent dark:text-indigo-400 dark:border-indigo-700 dark:hover:bg-indigo-900/20',
    text:      'bg-transparent text-blue-600 hover:bg-blue-50 hover:text-blue-700 focus:ring-blue-300 dark:text-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-300',
    action:    'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 focus:ring-blue-400 dark:bg-transparent dark:text-slate-300 dark:border-slate-700 dark:hover:bg-slate-800',
  };

  const variantCls = (outlined ? ghost : filled)[variant] ?? filled.primary;
  const isDisabled = disabled || busy;
  const disabledCls = isDisabled
    ? `opacity-50 ${busy ? 'cursor-wait' : 'cursor-not-allowed pointer-events-none'}`
    : 'cursor-pointer';
  const widthCls = fullWidth ? 'w-full' : '';

  return (
    <button
      type={type}
      className={`${base} ${sizes[size] ?? sizes.md} ${variantCls} ${widthCls} ${disabledCls} ${className}`}
      onClick={isDisabled ? undefined : handleClick}
      disabled={isDisabled}
      aria-busy={busy || undefined}
      {...rest}
    >
      {busy ? (
        <span
          aria-hidden="true"
          className="flex-shrink-0 inline-block w-3.5 h-3.5 rounded-full border-2 border-current border-t-transparent animate-spin"
        />
      ) : (
        icon && <span className="flex-shrink-0">{icon}</span>
      )}
      {busy && busyLabel ? busyLabel : children}
    </button>
  );
};

Button.propTypes = {
  variant: PropTypes.oneOf(['primary', 'secondary', 'danger', 'success', 'warning', 'info', 'text', 'action']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  children: PropTypes.node,
  onClick: PropTypes.func,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  className: PropTypes.string,
  icon: PropTypes.node,
  outlined: PropTypes.bool,
};

export default Button;
